import api from "./api";
import { ok, isoUtc, nameOf } from "./core";

export const DATA_SCOPES = [
  { value: "SELF", label: "Self (own records)" },
  { value: "TEAM", label: "Team (direct reports)" },
  { value: "PERFORMANCE_ORG", label: "Organisation (performance only)" },
  { value: "AGGREGATE_ONLY", label: "Aggregate reports only" },
  { value: "SYSTEM_ALL", label: "Everything (administration)" },
];

export async function listPermissions() {
  const { data } = await api.get("/permissions");
  return ok(data.map((p) => ({ id: p.permission_id, code: p.permission_code, module: p.module_code, description: p.description, active: Boolean(p.is_active) })));
}

const toRole = (r) => ({
  id: r.role_id,
  code: r.role_code,
  name: r.role_name,
  description: r.role_description || "",
  scope: r.data_scope,
  active: Boolean(r.is_active),
});

export async function listRoles() {
  const { data } = await api.get("/roles");
  return ok(data.map(toRole));
}

export const createRole = ({ code, name, description, scope, reason }) =>
  api.post("/roles", { role_code: code, role_name: name, role_description: description || "", data_scope: scope, reason: reason || "Role created" });

export const updateRole = (roleId, { name, description, scope, active, reason }) =>
  api.patch(`/roles/${roleId}`, { role_name: name, role_description: description || "", data_scope: scope, is_active: active, reason: reason || "Role updated" });

export async function getRolePermissions(roleId) {
  const { data } = await api.get(`/roles/${roleId}/permissions`);
  return ok(data.map((p) => p.permission_id));
}

export const grantPermission = (roleId, permissionId, reason) =>
  api.put(`/roles/${roleId}/permissions/${permissionId}`, { reason: reason || "Permission granted" });

export const revokePermission = (roleId, permissionId, reason) =>
  api.delete(`/roles/${roleId}/permissions/${permissionId}`, { data: { reason: reason || "Permission revoked" } });

export async function getEmployeeRoleAssignments(employeeId) {
  const { data } = await api.get(`/employees/${employeeId}/roles`);
  return ok(data.map((a) => ({
    id: a.user_role_id,
    roleId: a.role_id,
    roleCode: a.role_code,
    roleName: a.role_name,
    from: isoUtc(a.effective_from),
    to: isoUtc(a.effective_to),
    revokedAt: isoUtc(a.revoked_at),
    assignedBy: nameOf(a.assigned_by_employee_id),
    active: !a.revoked_at && (!a.effective_to || new Date(isoUtc(a.effective_to)) > new Date()),
  })));
}

export const assignRole = (employeeId, { roleId, from, to, reason }) =>
  api.post(`/employees/${employeeId}/roles`, {
    role_id: Number(roleId),
    effective_from: from ? new Date(from).toISOString() : undefined,
    effective_to: to ? new Date(to).toISOString() : undefined,
    reason: reason || "Role assigned",
  });

export const revokeRoleAssignment = (assignmentId, reason) => api.post(`/user-roles/${assignmentId}/revoke`, { reason: reason || "Role revoked" });

export const checkAccess = (permissionCode, employeeId) => api.post("/access/check", { permission_code: permissionCode, employee_id: employeeId });

// [NEW] PM-5: IT Admin = minimum organisation data for the IT department. The role is created with the existing
// role APIs the first time an administrator gives it to someone: sign-in accounts, invitations and the full audit
// trail, but no performance data (no ratings, KRAs, goals or assessments).
export const IT_ADMIN_PERMISSIONS = [
  "audit.security.read", "audit.security.export", "account.invite",
  "rbac.user_role.read", "rbac.role.read", "rbac.permission.read",
];

export async function ensureItAdminRole() {
  const { data: roles } = await api.get("/roles");
  let role = roles.find((r) => r.role_code === "IT_ADMIN");
  if (!role) {
    const { data } = await api.post("/roles", {
      role_code: "IT_ADMIN", role_name: "IT Admin", data_scope: "SYSTEM_ALL",
      role_description: "IT department: user accounts, invitations and audit trail. No performance data.",
      reason: "IT Admin role created (PM-5)",
    });
    role = data;
  }
  const [{ data: perms }, { data: granted }] = await Promise.all([api.get("/permissions"), api.get(`/roles/${role.role_id}/permissions`)]);
  const have = new Set(granted.map((p) => p.permission_id));
  for (const code of IT_ADMIN_PERMISSIONS) {
    const p = perms.find((x) => x.permission_code === code);
    if (p && !have.has(p.permission_id)) await api.put(`/roles/${role.role_id}/permissions/${p.permission_id}`, { reason: "IT Admin permissions" });
  }
  return { ...role, is_active: role.is_active ?? true };
}
